// RUSSIAN (ru), written by hand for the design partner's office (KazKabel,
// Almaty), whose Big Boss decides the agents' asks from his phone. Terms follow
// docs/i18n/glossary.json: «Утвердить» for approve, «Блокировка» for a quality
// hold, «Резерв» for a reservation, «Площадка» for a facility. A counted noun
// is not agreed with its number: the count follows a colon (D-030). The
// agent's own words are never here; they render in the untrusted frame.

import type { AgentsDict } from "./agents.i18n";

export const agentsRuDict: AgentsDict = {
  nav: {
    section: "Агенты",
    aria: "Разделы агентов",
    hub: "Агенты",
    journal: "Журнал",
  },
  common: {
    loading: "Загрузка агентов…",
    retry: "Повторить",
    requestId: "ID запроса",
    loadError: "Не удалось загрузить.",
    stale: "Показан последний ответ",
    save: "Сохранить",
    saving: "Сохранение…",
    cancel: "Отмена",
    close: "Закрыть",
    discard: "Отменить изменения",
    all: "Все",
    previous: "Назад",
    next: "Далее",
    pageRange: "{{from}}–{{to}}",
    pageOf: "{{from}}–{{to}} из {{total}}",
    sortToggle: "Сортировать по столбцу «{{column}}», {{direction}}",
    ascending: "по возрастанию",
    descending: "по убыванию",
    forbiddenTitle: "Нет доступа",
    forbiddenBody: "Ваша роль не открывает эту страницу.",
    notFoundTitle: "Агент не найден",
    notFoundBody: "Возможно, его удалили, или ваша роль его не видит.",
  },
  catalog: {
    ops_copilot: {
      name: "Помощник по операциям",
    },
    offer_agent: {
      name: "Агент предложений",
    },
    planner_agent: {
      name: "Агент планирования",
    },
    qc_watcher: {
      name: "Наблюдатель качества",
    },
    finance_clerk: {
      name: "Финансовый агент",
    },
  },
  roles: {
    agent_sales: "Агент продаж",
    agent_planner: "Агент планирования",
    agent_qc: "Агент качества",
    agent_finance: "Финансовый агент",
    agent_ops: "Операционный агент",
  },
  status: {
    enabled: "Включён",
    paused: "Приостановлен",
    disabled: "Отключён",
    pausedBy: "Приостановил(а) {{name}}, {{date}}",
    pausedOn: "Приостановлен {{date}}",
  },
  hub: {
    title: "Агенты",
    lede: "Что сделали ваши агенты и что им разрешено делать самим.",
    tabs: {
      aria: "Представления агентов",
      agents: "Агенты",
      guardrails: "Ограничители",
    },
    emptyTitle: "Агентов пока нет",
    emptyBody: "Агенты появятся здесь, когда в вашем пространстве будет хотя бы один.",
    loading: "Загрузка агентов…",
    loadError: "Не удалось загрузить агентов.",
    actions: "Действия за месяц",
    actionsOf: "{{used}} из {{cap}}",
    actionsNoCap: "{{used}}, без лимита",
    spend: "Расход за месяц",
    spendOf: "{{used}} из {{cap}}",
    capReached: "Лимит достигнут",
    tokens: "Токены за сегодня",
    tokensOf: "{{used}} из {{budget}}",
    lastRun: "Последний запуск",
    lastRunNone: "Ещё не запускался",
    activity: "Выполненные действия за 30 дней",
    viewJournal: "Открыть журнал",
    configure: "Настроить",
    enable: "Включить",
    pause: "Приостановить",
    toggleLabel: "{{name}}: включён",
    statusSaved: "{{name}}: теперь «{{status}}».",
    banner: {
      injection_one: "За последние 24 часа сообщение пыталось управлять агентом.",
      injection: "За последние 24 часа сообщения пытались управлять агентом: {{count}}.",
      spend_one: "За последние 24 часа агент достиг лимита расходов.",
      spend: "За последние 24 часа агенты достигали лимита расходов: {{count}}.",
      review: "Посмотреть ограничители",
    },
    readOnly: "Только просмотр",
  },
  runStatus: {
    queued: "В очереди",
    running: "Выполняется",
    awaiting_approval: "Ждёт утверждения",
    completed: "Завершён",
    failed: "Ошибка",
    cancelled: "Отменён",
  },
  trigger: {
    message: "Сообщение",
    event: "Событие",
    schedule: "Расписание",
    manual: "Вручную",
  },
  outcome: {
    drafted: "Подготовлен черновик КП",
    clarify_proposed: "Клиенту задан вопрос",
    status_proposed: "Предложен ответ о статусе заказа",
    not_a_quote_request: "Это не запрос КП",
    identity_unresolved: "Отправитель не найден среди клиентов",
    forwarded_not_processed: "Пересланное сообщение оставлено сотруднику",
    no_data_in_scope: "Нечего обрабатывать",
    no_owner: "У клиента нет ответственного менеджера",
  },
  guardrails: {
    title: "Ограничители",
    emptyTitle: "Срабатываний нет",
    emptyBody: "За этот период ничего не было остановлено.",
    loading: "Загрузка срабатываний…",
    loadError: "Не удалось загрузить срабатывания.",
    filters: {
      kind: "Вид",
      agent: "Агент",
      from: "С",
      to: "По",
      clear: "Сбросить фильтры",
    },
    columns: {
      when: "Когда",
      kind: "Вид",
      agent: "Агент",
      detail: "Подробности",
    },
    kind: {
      prompt_injection_suspected: "Сообщение пыталось управлять агентом",
      tool_denied_visibility: "Инструмент отклонён: нет доступа",
      tool_denied_policy: "Инструмент отклонён правилом",
      cap_action_exceeded: "Достигнут лимит действий",
      cap_spend_exceeded: "Достигнут лимит расходов",
      cap_token_exceeded: "Достигнут лимит токенов",
      schema_validation_failed: "Агент передал неверные данные",
      citation_missing: "Ответ без источника",
      scope_violation_blocked: "За пределами зоны агента",
      tool_orphaned: "Инструмент удалён",
      run_limit_exceeded: "Запуск остановлен на пределе",
    },
    detail: {
      pattern: "Совпадение: {{pattern}}",
      tool: "Инструмент: {{tool}}",
      capCount: "{{current}} из {{cap}}",
      tokens: "Токены: {{used}} из {{budget}}",
      attempt: "Попытка {{attempt}}",
      turnLimit: "Остановлен после {{value}} шагов",
      wallClock: "Остановлен через {{value}} с",
      spend: "{{spend}} из {{cap}}",
    },
    run: "Запуск",
    runActions: "Действия этого запуска",
    runNoActions: "Этот запуск ничего не предложил.",
    openCard: "Открыть карточку",
    expand: "Подробнее",
  },
  detail: {
    loading: "Загрузка агента…",
    loadError: "Не удалось загрузить агента.",
    back: "Агенты",
    displayName: "Название",
    rename: "Переименовать",
    renamePlaceholder: "Оставьте пустым для стандартного названия",
    renamed: "Название сохранено.",
    tabs: {
      aria: "Разделы агента",
      policies: "Правила инструментов",
      prompt: "Промпт",
    },
    memberships: {
      title: "Где работает",
      empty: "Зона не задана. Агент нигде не может действовать.",
      group: "Вся группа",
      facility: "{{facility}}",
      department: "{{facility}} · {{department}}",
      add: "Добавить зону",
      remove: "Убрать",
      scopeType: "Зона",
      facilityPick: "Площадка",
      departmentPick: "Подразделение",
      choose: "Выберите…",
      saved: "Зона сохранена.",
      scope: {
        group: "Группа",
        facility: "Площадка",
        department: "Подразделение",
      },
    },
    caps: {
      title: "Лимиты",
      monthlyActions: "Действий без надзора в месяц",
      monthlySpend: "Лимит расходов в месяц ({{currency}})",
      dailyTokens: "Токенов в день",
      saved: "Лимиты сохранены.",
      invalid: "Введите целое число не меньше нуля.",
      invalidMoney: "Введите сумму не меньше нуля.",
      ceiling: "Потолок для денежных инструментов у админов площадок ({{currency}})",
      ceilingSaved: "Потолок сохранён.",
    },
    policies: {
      loading: "Загрузка правил инструментов…",
      loadError: "Не удалось загрузить правила инструментов.",
      empty: "У этого агента нет инструментов, которые вам видны.",
      readOnlyRows: "Инструменты чтения работают без запроса.",
      columns: {
        tool: "Инструмент",
        autonomy: "Автономность",
        numbers: "Параметры правила",
        usage: "За 30 дней",
      },
      kind: {
        read: "Чтение",
        write: "Запись",
      },
      reversibility: {
        reversible: "Обратимо",
        compensatable: "Исправимо вручную",
        irreversible: "Необратимо",
      },
      usage: "Выполнено: {{executed}} · предложено: {{proposed}}",
      source: {
        declared: "По умолчанию",
        explicit: "Задано здесь",
      },
      orphaned: "Инструмент удалён обновлением платформы. Правило не действует.",
      dismiss: "Убрать",
      dismissed: "Правило убрано.",
      cap: "Лимит ({{currency}})",
      minReliability: "Минимальная надёжность (%)",
      facilityCap: "Лимит: {{facility}} ({{currency}})",
      addFacility: "Добавить площадку",
      noCaps: "Лимиты площадок не заданы.",
      removeFacility: "Убрать",
      spendCapWarning:
        "Агент не сможет действовать без надзора, пока не повышен месячный лимит расходов.",
      invalidNumber: "Введите число не меньше нуля.",
    },
    autonomy: {
      disabled: "Отключено",
      ask: "Спрашивать",
      threshold: "По порогу",
      autonomous_with_cap: "Самостоятельно в пределах лимита",
      autonomous: "Самостоятельно",
    },
    unsaved: {
      count_one: "Несохранённых изменений: {{count}}",
      count: "Несохранённых изменений: {{count}}",
      item: "{{tool}}: {{from}} → {{to}}",
      itemNumbers: "{{tool}}: параметры правила",
      saved_one: "Сохранено правил: {{count}}.",
      saved: "Сохранено правил: {{count}}.",
    },
    conflict: {
      title: "Это уже изменил кто-то другой",
      body: "Его изменение сохранено раньше. Оставьте его или примените своё поверх.",
      theirs: "Чужое",
      yours: "Ваше",
      keepTheirs: "Оставить чужое",
      applyMine: "Применить своё",
    },
    prompt: {
      loading: "Загрузка промпта…",
      loadError: "Не удалось загрузить промпт.",
      active: "Активный промпт",
      version: "Версия {{version}}",
      history: "Версии",
      columns: {
        version: "Версия",
        source: "Источник",
        changelog: "Изменение",
        created: "Создана",
        state: "Состояние",
      },
      source: {
        launch_pack: "Стандартная",
        platform_update: "Обновление платформы",
        tenant_override: "Ваша замена",
      },
      isActive: "Активна",
      activate: "Активировать",
      activated: "Версия {{version}} активна.",
      override: "Заменить",
      overrideTitle: "Заменить промпт",
      overrideBody: "После активации ваша версия заменит стандартный промпт.",
      promptText: "Промпт",
      changelog: "Что изменилось",
      confirmLabel: "Введите {{word}} для подтверждения",
      create: "Сохранить версию",
      created: "Версия сохранена. Активируйте её, чтобы использовать.",
      empty: "Версий промпта нет.",
    },
  },
  journal: {
    toolRemoved: "Инструмент удалён обновлением платформы",
    title: "Журнал",
    lede: "Все действия, которые агенты предложили или выполнили.",
    tabs: {
      aria: "Представления журнала",
      mine: "Ждут моего решения",
      proposed: "Все предложенные",
      executed: "Выполнены",
      rejected: "Отклонены",
      failed: "С ошибкой",
      expired: "Истекли",
      undone: "Отменены",
    },
    empty: {
      mine: "Ничего не ждёт вашего решения",
      other: "Здесь действий нет",
      body: "Карточки появятся здесь по мере работы агентов.",
    },
    loading: "Загрузка журнала…",
    loadError: "Не удалось загрузить журнал.",
    filters: {
      label: "Фильтр журнала",
      agent: "Агент",
      tool: "Инструмент",
      facility: "Площадка",
      department: "Подразделение",
      from: "С",
      to: "По",
      impact: "Эффект не менее ({{currency}})",
      impactNoCurrency: "Эффект не менее",
      clear: "Сбросить фильтры",
    },
    columns: {
      action: "Действие",
      agent: "Агент",
      impact: "Эффект",
      created: "Предложено",
      expires: "Истекает",
      status: "Статус",
      decide: "Решение",
    },
    status: {
      proposed: "Предложено",
      approved: "Утверждено",
      rejected: "Отклонено",
      executing: "Выполняется",
      executed: "Выполнено",
      failed: "Ошибка",
      undone: "Отменено",
      expired: "Истекло без решения",
    },
    impact: {
      count_one: "Позиций: {{count}}",
      count: "Позиций: {{count}}",
      schedule_one: "Переносов в графике: {{count}}",
      schedule: "Переносов в графике: {{count}}",
      message_one: "Сообщений: {{count}}",
      message: "Сообщений: {{count}}",
    },
    countdown: {
      days: "Осталось {{days}} д {{hours}} ч",
      hours: "Осталось {{hours}} ч {{minutes}} мин",
      minutes: "Осталось {{minutes}} мин",
      expired: "Истекло",
    },
    approve: "Утвердить",
    reject: "Отклонить",
    approved: "Утверждено.",
    approvedExecuting: "Утверждено. Сейчас выполняется.",
    rejected: "Отклонено.",
    rejectTitle: "Отклонить действие",
    rejectNote: "Комментарий (необязательно)",
    note: {
      label: "Комментарий агента",
      frame: "Написано агентом. Проверьте, прежде чем действовать.",
    },
    run: {
      label: "Запуск",
      line: "{{trigger}} · {{status}}",
      error: "Завершён так: {{error}}",
    },
    evidence: "Подробнее",
    params: "Что будет сделано",
    result: "Открыть результат",
    undo: "Отменить",
    undoTitle: "Отменить действие",
    undoBody: "Это отменит сделанное агентом. В журнале останутся обе записи.",
    undoConfirm: "Отменить",
    undone: "Отменено.",
    manualTitle: "Отменить вручную",
    manualOpen: "Открыть запись",
    bulk: {
      count_one: "Выбрано: {{count}}",
      count: "Выбрано: {{count}}",
      clear: "Сбросить",
      approve: "Утвердить выбранные",
      done_one: "Утверждено: {{count}}.",
      done: "Утверждено: {{count}}.",
      partial: "Утверждено: {{ok}}, отказано: {{failed}}.",
      select: "Выбрать",
      selectAll: "Выбрать все, что можно утвердить",
      limit: "Не более {{max}} карточек одного инструмента за раз.",
    },
    focus: {
      title: "Открытая карточка",
      close: "Показать все",
      missing: "Этой карточки нет в вашем журнале.",
    },
    template: {
      openQuote: "Открыть КП",
      openOrder: "Открыть заказ",
      body: "Сообщение",
      lines: "Строки КП",
      fill: {
        offcut_combo: "Из обрезков",
        stock_unit: "Из складской единицы",
        production: "В производство",
      },
      perUnit: "{{price}} за ед.",
      alternatives: "Варианты исполнения строк",
      lineNo: "Строка {{no}}",
      pieces_one: "Отрезков: {{count}}",
      pieces: "Отрезков: {{count}}",
      chosen: "Взят в черновик",
      quoteNo: "КП {{no}}",
      noChannel: "Ни один канал не доставит это",
      marginBreached: "Ниже порога маржи",
      offList_one: "Строк вне прайса: {{count}}",
      offList: "Строк вне прайса: {{count}}",
      total: "Итого",
      totalOfCap: "{{total}}, лимит {{cap}}",
      reliability: "Платёжная надёжность",
      reliabilityUnknown: "Нет оценки",
      reliabilityOf: "{{score}}, минимум {{min}}",
      sends: "Отправлено агентом за 7 дней",
      sendsOf_one: "{{count}} из {{limit}}",
      sendsOf: "{{count}} из {{limit}}",
      linesCount_one: "Строк: {{count}}",
      linesCount: "Строк: {{count}}",
      orderNo: "Заказ {{no}}",
      customerGets: "Клиент получит:",
      statusPreview: "Заказ {{no}}: {{status}}",
      openConversation: "Открыть переписку",
    },
    redraft: {
      button: "Подготовить КП",
      busy: "Запуск…",
      started: "Агент снова готовит КП. Новая карточка появится здесь.",
      noMessage: "Сообщение, из которого возникла карточка, больше не найдено.",
    },
    evidenceKey: {
      impact_count: "Позиции",
      shortage_count: "Нехватки",
      qty_planned: "Плановое количество",
      items: "Позиции",
      max_autonomous_items: "Максимум позиций без надзора",
      cap: "Лимит",
      used: "Использовано",
      remaining: "Осталось",
    },
    applied: {
      ask: "Запрошено по правилу",
      forced_ask: "Запрошено: агент не мог действовать сам",
      threshold_pass: "В пределах порога",
      threshold_exceeded_ask: "Выше порога",
      cap_exceeded_ask: "Выше лимита",
      autonomous: "Выполнено самостоятельно",
      autonomous_with_cap: "Выполнено самостоятельно в пределах лимита",
      human_initiated: "Запущено сотрудником",
    },
  },
  problems: {
    AGENT_AUTONOMY_ASK_REQUIRED: "Этому инструменту нужно утверждение сотрудника.",
    AGENT_AUTONOMY_CAP_EXCEEDED: "Это превысит лимит агента.",
    AGENT_AUTONOMY_THRESHOLD_EXCEEDED: "Это выше порога инструмента.",
    AGENT_AUTONOMY_THRESHOLD_UNKNOWN: "Порог не удалось измерить.",
    AGENT_AUTONOMY_NOT_ALLOWED: "Такая автономность выше разрешённой для инструмента.",
    AGENT_TOOL_NOT_AVAILABLE: "Этот инструмент недоступен.",
    AGENT_TOOL_PARAMS_INVALID: "Агент передал данные, которые инструмент отклонил.",
    AGENT_TOOL_TENANT_PARAM_FORBIDDEN: "Агент указал чужое рабочее пространство.",
    AGENT_TOOL_INVARIANT_BROKEN: "Инструмент остановился, чтобы защитить ваши данные.",
    AGENT_PRINCIPAL_UNBOUND: "Агент действовал без собственной учётной записи и был остановлен.",
    AGENT_IRREVERSIBLE_NOT_AUTONOMOUS: "Необратимый инструмент не может действовать сам.",
    AGENT_NOT_FOUND: "Этого агента больше нет.",
    AGENT_VERSION_CONFLICT: "Агента изменил кто-то другой. Обновите страницу и повторите.",
    AGENT_PROFILE_UNKNOWN: "Этот профиль модели недоступен.",
    AGENT_TOOLS_MISSING: "Этого агента пока нельзя включить.",
    AGENT_BINDING_OUTSIDE_SCOPE: "Нельзя выдать зону за пределами вашей.",
    AGENT_TOOL_NOT_FOUND: "Этого инструмента нет в списке агента.",
    AGENT_POLICY_LAXER_REQUIRES_CONFIG: "Ослабить это правило может только администратор группы.",
    AGENT_FACILITY_CEILING_EXCEEDED: "Это выше денежного потолка для админов площадок.",
    AGENT_POLICY_VERSION_CONFLICT: "Это правило изменил кто-то другой.",
    AGENT_POLICY_NOT_ORPHANED: "Инструмент этого правила ещё существует.",
    AGENT_PROMPT_VERSION_NOT_FOUND: "Этой версии промпта больше нет.",
    AGENT_PROMPT_ACTIVATION_CONFLICT: "Раньше активировали другую версию.",
    AGENT_TRIGGER_INVALID: "Агент не может начать с этого.",
    AGENT_RUN_SUBJECT_NOT_FOUND: "Запись, с которой работал бы агент, вам не видна.",
    AGENT_PAUSED: "Этот агент приостановлен.",
    AGENT_TOKEN_BUDGET_EXHAUSTED: "Дневной бюджет агента исчерпан.",
    AGENT_PLATFORM_TOKEN_CEILING_REACHED: "Агенты достигли дневного предела. Повторите завтра.",
    AGENT_RUN_NOT_FOUND: "Этого запуска больше нет.",
    AGENT_RUN_NOT_CANCELLABLE: "Этот запуск уже завершён.",
    AGENT_IMPACT_FILTER_REQUIRES_VALUE: "Ваша роль не может фильтровать по сумме.",
    AGENT_ACTION_NOT_FOUND: "Этой карточки нет в вашем журнале.",
    AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY: "Нельзя утвердить сумму, которую ваша роль не видит.",
    AGENT_ACTION_EXPIRED: "Срок этой карточки истёк до решения.",
    AGENT_ACTION_ALREADY_DECIDED: "По этой карточке уже приняли решение.",
    AGENT_ACTION_NOT_UNDOABLE: "Это действие нельзя отменить здесь.",
    AGENT_TOOL_DISABLED: "Этот инструмент отключён для агента.",
    AGENT_RUN_TURN_LIMIT: "Запуск исчерпал лимит шагов и остановлен.",
    AGENT_RUN_WALL_CLOCK: "Запуск шёл слишком долго и остановлен.",
    AGENT_TOOL_NOT_VISIBLE: "Агент не может использовать этот инструмент.",
    AGENT_MODEL_UNAVAILABLE: "Модель агента не ответила.",
    AGENT_MODEL_ACCESS_DENIED: "Нет связи с моделью агента.",
    AGENT_MODEL_REPLAY_DIVERGED: "Записанный ответ агента не совпал.",
    AGENT_RUN_INTERRUPTED: "Запуск был прерван.",
    AGENT_TOOL_FAILED_TWICE: "Инструмент дважды завершился с ошибкой, запуск остановлен.",
    AGENT_TEXT_CARRIES_PRICE: "В сообщении была цена, поэтому оно не отправлено.",
    AGENT_SUBJECT_UNOWNED: "У клиента нет ответственного менеджера.",
    PERMISSION_DENIED: "Ваша роль не может это сделать.",
    other: "Отклонено.",
  },
  tools: {
    catalogue: {
      add_product_conversion: {
        name: "Добавить пересчёт единиц",
      },
      create_product_draft: {
        name: "Создать черновик продукта",
      },
      draft_bom_version: {
        name: "Создать черновик версии спецификации",
      },
      draft_routing_version: {
        name: "Создать черновик версии маршрута",
      },
      explain_cost: {
        name: "Объяснить себестоимость",
      },
      get_bom_tree: {
        name: "Прочитать дерево спецификации",
      },
      get_change_history: {
        name: "Прочитать историю изменений",
      },
      get_product: {
        name: "Прочитать продукт",
      },
      get_routing: {
        name: "Прочитать маршрут",
      },
      search_products: {
        name: "Найти продукты",
      },
      set_component_scrap: {
        name: "Задать отход компонента",
      },
      start_cost_rollup: {
        name: "Запустить расчёт себестоимости",
      },
      where_used: {
        name: "Найти, где используется деталь",
      },
    },
    inventory: {
      adjust_stock: {
        name: "Скорректировать остаток",
      },
      create_transfer_draft: {
        name: "Создать черновик перемещения",
      },
      flag_quarantine: {
        name: "Поместить запас в карантин",
      },
      get_atp: {
        name: "Прочитать доступное к обещанию",
      },
      get_stock_unit: {
        name: "Прочитать единицу запаса",
      },
      list_in_transit: {
        name: "Список запасов в пути",
      },
      list_open_variances: {
        name: "Список открытых расхождений пересчёта",
      },
      lookup_stock: {
        name: "Найти остатки",
      },
      release_reservation: {
        name: "Снять резерв",
      },
      request_cycle_count: {
        name: "Запросить циклический пересчёт",
      },
      reserve_stock: {
        name: "Зарезервировать запас",
      },
      trace_lot: {
        name: "Отследить партию",
      },
    },
    messaging: {
      check_send_viability: {
        name: "Проверить, дойдёт ли сообщение",
      },
      get_thread: {
        name: "Прочитать переписку",
      },
      send_text: {
        name: "Отправить сообщение",
      },
    },
    onboarding: {
      apply_column_mapping: {
        name: "Применить сопоставление столбцов импорта",
      },
      commit_import: {
        name: "Провести импорт",
      },
      fix_import_rows: {
        name: "Исправить строки импорта",
      },
      generate_qr_kit: {
        name: "Сформировать комплект QR-кодов",
      },
      get_checklist: {
        name: "Прочитать чек-лист запуска",
      },
      get_import_errors: {
        name: "Прочитать ошибки импорта",
      },
      get_import_job: {
        name: "Прочитать импорт",
      },
      get_status: {
        name: "Прочитать статус настройки",
      },
      get_terminology: {
        name: "Прочитать терминологию",
      },
      list_packs: {
        name: "Список отраслевых пакетов",
      },
      preview_pack: {
        name: "Просмотреть отраслевой пакет",
      },
      propose_column_mapping: {
        name: "Предложить сопоставление столбцов импорта",
      },
      propose_plant_map: {
        name: "Предложить схему завода",
      },
      run_validation: {
        name: "Проверить импорт",
      },
      waive_checklist_item: {
        name: "Пропустить пункт чек-листа",
      },
    },
    planning: {
      accept_action_message: {
        name: "Принять сообщение MRP",
      },
      approve_mps: {
        name: "Утвердить главный график",
      },
      commit_promise: {
        name: "Закрепить обещанную дату поставки",
      },
      convert_planned_orders: {
        name: "Преобразовать плановые заказы",
      },
      create_scenario: {
        name: "Создать сценарий «что если»",
      },
      diff_scenario: {
        name: "Сравнить сценарий",
      },
      explain_planned_order: {
        name: "Объяснить плановый заказ",
      },
      firm_planned_orders: {
        name: "Закрепить плановые заказы",
      },
      get_action_messages: {
        name: "Прочитать сообщения MRP",
      },
      get_capacity_load: {
        name: "Прочитать загрузку мощностей",
      },
      get_demand_pool: {
        name: "Прочитать пул спроса",
      },
      get_mps: {
        name: "Прочитать главный график",
      },
      get_promises: {
        name: "Прочитать обещанные даты поставки",
      },
      get_schedule: {
        name: "Прочитать график",
      },
      probe_promise: {
        name: "Проверить дату поставки",
      },
      promote_scenario: {
        name: "Применить сценарий",
      },
      quote_promise: {
        name: "Предложить дату поставки",
      },
      refresh_demand: {
        name: "Обновить пул спроса",
      },
      reschedule_operation: {
        name: "Перенести операцию",
      },
      run_mrp: {
        name: "Запустить MRP",
      },
    },
    production: {
      cancel_work_order: {
        name: "Отменить заказ на производство",
      },
      create_work_order: {
        name: "Создать заказ на производство",
      },
      get_defect_handoff: {
        name: "Прочитать передачу брака",
      },
      get_machine_drift: {
        name: "Прочитать дрейф станка",
      },
      get_shift_scoreboard: {
        name: "Прочитать табло смены",
      },
      get_wo_cost_summary: {
        name: "Прочитать затраты заказа на производство",
      },
      get_work_order: {
        name: "Прочитать заказ на производство",
      },
      list_defect_handoffs: {
        name: "Список передач брака",
      },
      list_open_conflicts: {
        name: "Список открытых конфликтов цеха",
      },
      list_work_orders: {
        name: "Список заказов на производство",
      },
      raise_drift_exception: {
        name: "Зарегистрировать исключение по дрейфу",
      },
      release_work_order: {
        name: "Выдать заказ на производство в цех",
      },
      request_defect_disposition: {
        name: "Запросить решение по браку",
      },
      set_priority: {
        name: "Задать приоритет заказа на производство",
      },
    },
    quality: {
      create_inspection: {
        name: "Создать проверку",
      },
      draft_coa: {
        name: "Подготовить сертификат анализа",
      },
      execute_disposition: {
        name: "Выполнить решение по блокировке",
      },
      get_coa_document: {
        name: "Прочитать сертификат анализа",
      },
      get_failure_pareto: {
        name: "Прочитать Парето отказов",
      },
      get_fpy: {
        name: "Прочитать выход с первого раза",
      },
      get_hold: {
        name: "Прочитать блокировку качества",
      },
      get_inspection: {
        name: "Прочитать проверку",
      },
      get_shift_scoreboard: {
        name: "Прочитать табло качества",
      },
      get_ship_readiness: {
        name: "Проверить готовность к отгрузке",
      },
      get_supplier_receiving_stats: {
        name: "Прочитать итоги входного контроля поставщиков",
      },
      issue_coa: {
        name: "Выпустить сертификат анализа",
      },
      list_active_holds: {
        name: "Список активных блокировок качества",
      },
      list_open_inspections: {
        name: "Список открытых проверок",
      },
      place_hold: {
        name: "Наложить блокировку качества",
      },
      propose_disposition: {
        name: "Предложить решение по блокировке",
      },
      propose_guided_cut: {
        name: "Предложить разрез по дефекту",
      },
      request_rework: {
        name: "Запросить доработку",
      },
    },
    sales: {
      check_fulfillment_options: {
        name: "Проверить варианты исполнения",
      },
      draft_quote: {
        name: "Подготовить черновик КП",
      },
      get_customer_360: {
        name: "Прочитать клиента",
      },
      get_customer_pipeline: {
        name: "Прочитать сделки клиента",
      },
      get_order_status: {
        name: "Прочитать статус заказа",
      },
      get_quote: {
        name: "Прочитать КП",
      },
      lookup_customer_by_identity: {
        name: "Найти клиента по сообщению",
      },
      reply_order_status: {
        name: "Ответить статусом заказа",
      },
      resolve_price: {
        name: "Рассчитать цену продукта",
      },
      send_quote: {
        name: "Отправить КП",
      },
    },
    waste: {
      get_summary: {
        name: "Прочитать сводку отходов",
      },
      record_event: {
        name: "Записать событие отходов",
      },
      set_disposition: {
        name: "Задать решение по отходам",
      },
    },
  },
  compensation: {
    catalogue: {
      add_product_conversion: "Измените или удалите пересчёт в карточке продукта.",
      create_product_draft: "Удалите черновик продукта в каталоге.",
      draft_bom_version: "Удалите черновик версии спецификации.",
      draft_routing_version: "Удалите черновик версии маршрута.",
      set_component_scrap: "Верните отход в строке черновика спецификации.",
      start_cost_rollup: "Не выпускайте расчёт себестоимости или удалите его.",
    },
    inventory: {
      adjust_stock: "Сторнируйте корректировку в журнале движений.",
      create_transfer_draft: "Отмените черновик перемещения.",
      flag_quarantine: "Снимите партию с карантина.",
      release_reservation: "Снова зарезервируйте запас под потребность.",
      request_cycle_count: "Отмените пересчёт.",
    },
    onboarding: {
      apply_column_mapping: "Измените сопоставление столбцов в импорте.",
      commit_import: "Исправьте или архивируйте записи, созданные импортом.",
      fix_import_rows: "Снова отредактируйте строки в импорте.",
      generate_qr_kit: "Не используйте этот комплект. Его печать больше ничего не меняет.",
      propose_column_mapping: "Измените сопоставление столбцов в импорте.",
      propose_plant_map: "Отредактируйте черновик схемы завода в настройке.",
      run_validation: "Проверьте импорт снова после исправлений.",
      waive_checklist_item: "Снова откройте пункт в чек-листе запуска.",
    },
    planning: {
      accept_action_message: "Верните плановый заказ на экране плановых заказов.",
      approve_mps: "Измените главный график и утвердите новую версию.",
      commit_promise: "Отзовите обещание на экране обещаний.",
      convert_planned_orders: "Отмените созданные черновики заказов на производство и перемещений.",
      create_scenario: "Удалите сценарий.",
      firm_planned_orders: "Отмените ненужные закреплённые плановые заказы.",
      promote_scenario: "Удалите внесённые изменения календаря и пересчитайте график.",
      quote_promise: "Дождитесь истечения предложенной даты. Она ничего не резервирует.",
      refresh_demand: "Отменять нечего. Следующее обновление заменит это.",
      reschedule_operation: "Верните операцию на доске графика.",
      run_mrp: "Отменять нечего. Следующий запуск MRP заменит этот.",
    },
    production: {
      create_work_order: "Отмените черновик заказа на производство.",
      raise_drift_exception: "Закройте исключение.",
      release_work_order: "Отмените заказ на производство, если он не должен выполняться.",
      request_defect_disposition: "Отзовите предложение в передаче брака.",
      set_priority: "Верните приоритет в заказе на производство.",
    },
    quality: {
      create_inspection: "Отмените проверку.",
      draft_coa: "Удалите черновик сертификата.",
      place_hold: "Снимите блокировку на доске карантина.",
      propose_disposition: "Уберите предложенное решение в блокировке.",
      propose_guided_cut: "Уберите предложенный разрез по дефекту в проверке.",
      request_rework: "Завершите или отмените вновь открытую операцию в заказе на производство.",
    },
    sales: {
      draft_quote: "Отмените черновик КП.",
      send_quote: "Отмените КП, чтобы отозвать ссылку, или сохраните новую версию и отправьте её.",
    },
    waste: {
      record_event: "Сторнируйте событие в журнале отходов.",
      set_disposition: "Измените решение в событии отходов.",
    },
  },
};
